import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { fetchConfig } from '@theme/utilities';

const SOURCE = 'aov-cart-drawer';
const INNER_ID = 'AovDrawerInner';

/**
 * The theme defines `const Theme = {...}` in a classic script: a global
 * lexical binding, not a window property. Resolve it either way.
 */
function themeGlobal() {
  // eslint-disable-next-line no-undef
  return (typeof Theme !== 'undefined' ? Theme : window.Theme) || {};
}

function themeRoutes() {
  return themeGlobal().routes || {};
}

/**
 * AOV cart drawer. Extends the theme's DialogComponent (native <dialog>,
 * top layer, scroll lock, Esc, click-outside). Content is server-rendered
 * Liquid; after any cart mutation the inner container is re-rendered via the
 * Section Rendering API and morphed in place. The <dialog> element itself is
 * never morphed, which preserves its open state.
 *
 * @extends {DialogComponent}
 */
class AovCartDrawer extends DialogComponent {
  /** Offer ids already reported as impressions this open. */
  #seenOffers = new Set();
  #refreshPromise = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.addEventListener('click', this.#onClick);
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    this.addEventListener('submit', this.#onSubmit);

    if (this.hasAttribute('hijack-links')) {
      document.addEventListener('click', this.#onDocumentClick, true);
    }

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.closeDialog);
    }

    // Catch carts dirtied before this element existed (PDP adds, old tabs).
    if (this.limitOne) setTimeout(() => this.#refresh(), 1500);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    this.removeEventListener('submit', this.#onSubmit);
    document.removeEventListener('click', this.#onDocumentClick, true);

    if (window.Shopify?.designMode) {
      document.removeEventListener('shopify:section:select', this.#onEditorSelect);
      document.removeEventListener('shopify:section:deselect', this.closeDialog);
    }
  }

  get sectionId() {
    return this.dataset.sectionId || 'aov-cart-drawer';
  }

  get limitOne() {
    return this.hasAttribute('limit-one');
  }

  get inner() {
    return this.querySelector(`#${INNER_ID}`);
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  /**
   * Public refresh, used by the UpCart shim for refresh-only signals.
   */
  refresh() {
    return this.#refresh();
  }

  /**
   * Refresh drawer content, then open. Public so the UpCart shim and promo
   * CTAs can call one method for "something changed, show the cart".
   */
  async refreshAndOpen() {
    await this.#refresh();
    this.showDialog();
  }

  /**
   * Cart changed somewhere in the theme (product form, quick add, cart page).
   * Refresh our content; auto-open unless the change came from us or from the
   * cart page's own quantity edits.
   * @param {CustomEvent} event
   */
  #onCartUpdate = async (event) => {
    const detail = event.detail ?? {};
    const source = detail.data?.source ?? detail.sourceId;

    if (source === SOURCE) return;
    if (detail.data?.didError) return;

    const fromCartPage = detail.data?.source === 'cart-items-component';

    if (this.hasAttribute('auto-open') && !fromCartPage && !this.refs.dialog.open) {
      this.showDialog();
    }

    await this.#refresh();
  };

  /**
   * limit-one enforcement: any line with quantity above 1 drops to 1, and
   * extra lines of an already-present product (variant differences included)
   * are removed, keeping the newest. Runs on external cart changes so adds
   * from PDP buttons and the /cart page are corrected within a moment.
   * @returns {Promise<boolean>} true if the cart was changed (and re-rendered).
   */
  async #normalizeQuantities() {
    if (!this.limitOne) return false;

    try {
      const cart = await (await fetch(`${themeRoutes().cart_url || '/cart'}.js`)).json();
      const updates = {};
      const seenProducts = new Set();

      for (const item of cart.items) {
        if (seenProducts.has(item.product_id)) {
          updates[item.key] = 0;
          continue;
        }
        seenProducts.add(item.product_id);
        if (item.quantity > 1) updates[item.key] = 1;
      }

      if (Object.keys(updates).length === 0) return false;

      const response = await fetch(`${themeRoutes().cart_update_url || '/cart/update'}.js`, {
        ...fetchConfig('json', {
          body: JSON.stringify({ updates, sections: this.sectionId }),
        }),
      });
      const updated = await response.json();

      if (updated.status) throw new Error(updated.message || 'Cart error');

      this.#morphInner(updated.sections?.[this.sectionId]);
      this.#dispatchCartUpdate(updated);
      return true;
    } catch (error) {
      console.error('[aov-cart-drawer] quantity normalize failed', error);
      return false;
    }
  }

  #onEditorSelect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) {
      this.showDialog();
    }
  };

  /**
   * Delegated clicks inside the drawer: close, remove line, empty-state add.
   * Delegation lives on the custom element so it survives content morphs.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const closeTrigger = target.closest('[data-aov-close]');
    if (closeTrigger) {
      if (closeTrigger.tagName === 'BUTTON') event.preventDefault();
      this.closeDialog();
      return;
    }

    const removeTrigger = target.closest('[data-aov-remove]');
    if (removeTrigger) {
      event.preventDefault();
      this.#removeLine(removeTrigger.getAttribute('data-aov-remove'), removeTrigger);
      return;
    }

    const addTrigger = target.closest('[data-aov-add]');
    if (addTrigger) {
      event.preventDefault();
      const offerId = addTrigger.getAttribute('data-aov-offer-id');
      const replaceKeys = (addTrigger.getAttribute('data-aov-replace') || '').split(',').filter(Boolean);
      let source = 'empty-state';
      if (offerId) source = replaceKeys.length ? `upgrade:${offerId}` : `offer:${offerId}`;
      this.#addVariant(addTrigger.getAttribute('data-aov-add'), addTrigger, source, offerId, replaceKeys);
    }
  };

  #onDialogOpen = () => {
    this.#track('aov_drawer_open', {});
    this.#trackImpressions();
  };

  #onDialogClose = () => {
    this.#seenOffers.clear();
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (event.target instanceof HTMLFormElement && event.target.classList.contains('aov-drawer__checkout-form')) {
      this.#track('aov_checkout_click', {});
    }
  };

  /**
   * Capture-phase document listener: plain left-clicks on links to the cart
   * page open the drawer instead. Modifier clicks, new-tab links, and links
   * inside the drawer pass through untouched.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a[href]');
    if (!link || this.contains(link)) return;
    if (link.target && link.target !== '_self') return;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch {
      return;
    }

    if (url.origin !== window.location.origin) return;

    const cartPath = themeRoutes().cart_url || '/cart';
    const pathname = url.pathname.replace(/\/$/, '') || '/';
    if (pathname !== cartPath) return;

    event.preventDefault();
    this.refreshAndOpen();
  };

  #refresh() {
    if (this.#refreshPromise) return this.#refreshPromise;
    this.#refreshPromise = this.#performRefresh().finally(() => {
      this.#refreshPromise = null;
    });
    return this.#refreshPromise;
  }

  async #performRefresh() {
    this.setAttribute('aria-busy', 'true');

    try {
      // Legacy PDP and UpCart callers must enforce the same limit as native cart events.
      if (await this.#normalizeQuantities()) return;
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false);
      this.#morphInner(html);
    } catch (error) {
      console.error('[aov-cart-drawer] refresh failed', error);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string} html - Rendered section HTML from the Section Rendering
   *   API or a cart AJAX `sections` payload.
   */
  #morphInner(html) {
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.getElementById(INNER_ID);
    const current = this.inner;

    if (!fresh || !current) return;

    morph(current, fresh);
    this.#restoreFocus();
    this.#trackImpressions();
  }

  #restoreFocus() {
    if (!this.refs.dialog.open) return;
    if (this.contains(document.activeElement) && document.activeElement !== document.body) return;

    const fallback = this.querySelector('[data-aov-remove]') || this.querySelector('[data-aov-close]');
    if (fallback instanceof HTMLElement) fallback.focus();
  }

  /**
   * @param {string | null} key - Cart line item key.
   * @param {Element | null} trigger - The remove button, disabled in flight.
   */
  async #removeLine(key, trigger) {
    if (!key) return;

    trigger?.setAttribute('disabled', '');
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${themeRoutes().cart_change_url || '/cart/change'}.js`, {
        ...fetchConfig('json', {
          body: JSON.stringify({ id: key, quantity: 0, sections: this.sectionId }),
        }),
      });
      const cart = await response.json();

      if (cart.status) throw new Error(cart.message || cart.description || 'Cart error');

      this.#morphInner(cart.sections?.[this.sectionId]);
      this.#announce('Item removed');
      this.#dispatchCartUpdate(cart);
    } catch (error) {
      console.error('[aov-cart-drawer] remove failed', error);
      trigger?.removeAttribute('disabled');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string | null} variantId - Variant to add (offers, empty-state cards).
   * @param {Element | null} trigger - The add button, disabled in flight.
   * @param {string} [source] - Attribution written to the hidden _aov_source line property.
   * @param {string | null} [offerId] - Offer block id when the add came from an offer card.
   * @param {string[]} [replaceKeys] - Cart line keys to remove after the add (upgrade mode).
   */
  async #addVariant(variantId, trigger, source = 'drawer', offerId = null, replaceKeys = []) {
    if (!variantId) return;

    trigger?.setAttribute('disabled', '');
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(themeRoutes().cart_add_url || '/cart/add.js', {
        ...fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1, properties: { _aov_source: source } }],
            sections: this.sectionId,
          }),
        }),
      });
      const result = await response.json();

      if (result.status) throw new Error(result.message || result.description || 'Cart error');

      let sectionsHtml = result.sections?.[this.sectionId];

      if (replaceKeys.length) {
        // Upgrade: remove the covered lines in one batched update. If it
        // fails, the customer keeps both items rather than losing the add.
        try {
          const updates = {};
          for (const key of replaceKeys) updates[key] = 0;

          const swapResponse = await fetch(`${themeRoutes().cart_update_url || '/cart/update'}.js`, {
            ...fetchConfig('json', {
              body: JSON.stringify({ updates, sections: this.sectionId }),
            }),
          });
          const swapped = await swapResponse.json();

          if (!swapped.status && swapped.sections?.[this.sectionId]) {
            sectionsHtml = swapped.sections[this.sectionId];
          }
        } catch (error) {
          console.error('[aov-cart-drawer] upgrade swap failed, covered items kept', error);
        }
      }

      // Reflect the successful mutation before the optional count refresh.
      // A throttled count request must not leave stale items and totals visible.
      this.#morphInner(sectionsHtml);
      let cart;
      try {
        const cartResponse = await fetch(`${themeRoutes().cart_url || '/cart'}.js`);
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (error) {
        console.warn('[aov-cart-drawer] cart count refresh unavailable', error);
      }
      this.#announce(replaceKeys.length ? 'Upgraded' : themeGlobal().translations?.added || 'Added to cart');
      if (cart) this.#dispatchCartUpdate(cart);

      if (offerId) {
        this.#track('aov_offer_add', {
          offer_id: offerId,
          variant_id: variantId,
          mode: replaceKeys.length ? 'upgrade' : 'add',
          cart_total: cart?.total_price,
        });
      }
    } catch (error) {
      console.error('[aov-cart-drawer] add failed', error);
      trigger?.removeAttribute('disabled');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Absolute item count with our source so cart-icon sets rather than adds,
   * and so our own cart:update listener ignores the echo.
   * @param {object} cart - Cart JSON from the AJAX API.
   */
  #dispatchCartUpdate(cart) {
    document.dispatchEvent(
      new CartUpdateEvent(cart, SOURCE, {
        itemCount: cart.item_count,
        source: SOURCE,
      })
    );
  }

  /** @param {string} message */
  #announce(message) {
    const live = this.querySelector('[data-aov-live]');
    if (live) live.textContent = message;
  }

  /**
   * Report each visible offer card once per drawer-open session.
   */
  #trackImpressions() {
    if (!this.refs.dialog.open) return;

    for (const card of this.querySelectorAll('div[data-aov-offer-id]')) {
      const id = card.getAttribute('data-aov-offer-id');
      if (!id || this.#seenOffers.has(id)) continue;
      this.#seenOffers.add(id);
      this.#track('aov_offer_impression', {
        offer_id: id,
        product: card.getAttribute('data-aov-offer-product') || undefined,
      });
    }
  }

  /**
   * dataLayer push, gated by the section's analytics setting.
   * @param {string} name
   * @param {Record<string, unknown>} data
   */
  #track(name, data) {
    if (!this.hasAttribute('data-analytics')) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...data });
  }
}

if (!customElements.get('aov-cart-drawer')) {
  customElements.define('aov-cart-drawer', AovCartDrawer);
}
